import type {
	ExtendedFetchOptions
} from '@/types/api-client'

import type {
	InternalDeviceRebootPayload,
	InternalDeviceRebootResponse,
	InternalDeviceStatusPayload,
	InternalDeviceStatusResponse
} from '@/types/api-client/internal-device-tools'

/**
 * Creates internal device tools API client for CSR/technician portal
 * Uses /portal/accounts/ routes that return full technical details (no whitelist filtering)
 *
 * @param request - HTTP request handler with auto-error handling
 * @param createApiState - Factory for creating reactive API state
 * @returns API client object with device tools methods
 */
export function createInternalDeviceToolsApi(
	request: <T>(endpoint: string, options?: ExtendedFetchOptions) => Promise<T | null>
) {
	return {
		/**
		 * Check device status for internal portal (returns full technical details)
		 * Endpoint returns complete device information without whitelist filtering
		 *
		 * @param accountId - Account identifier from route params
		 * @param payload - Device information (MAC address, optional serial number, optional NMS type)
		 * @param options - Fetch configuration with auto-error handling options
		 * @returns Promise<InternalDeviceStatusResponse> Device status with full technical details
		 *
		 * @example
		 * const result = await $api.internalDeviceTools.checkDeviceStatus(123, {
		 *   macAddress: '00:11:22:33:44:55',
		 *   serialNumber: 'ABC123',
		 *   deviceNms: 'calix'
		 * })
		 */
		checkDeviceStatus: (
			accountId: number,
			payload: InternalDeviceStatusPayload,
			options: ExtendedFetchOptions = {}
		) => {
			return request<InternalDeviceStatusResponse>(
				`/portal/accounts/${accountId}/deviceStatus`,
				{
					method: 'POST',
					body: {
						accountId,
						...payload
					},
					...options
				}
			)
		},

		/**
		 * Reboot device for internal portal
		 * Triggers device reboot operation for supported NMS types (Calix, UISP)
		 *
		 * @param accountId - Account identifier from route params
		 * @param payload - Device information (MAC address, optional serial number, optional NMS type)
		 * @param options - Fetch configuration with auto-error handling options
		 * @returns Promise<InternalDeviceRebootResponse> Success status
		 *
		 * @example
		 * const result = await $api.internalDeviceTools.rebootDevice(123, {
		 *   macAddress: '00:11:22:33:44:55',
		 *   serialNumber: 'ABC123',
		 *   deviceNms: 'uisp'
		 * })
		 */
		rebootDevice: (
			accountId: number,
			payload: InternalDeviceRebootPayload,
			options: ExtendedFetchOptions = {}
		) => {
			return request<InternalDeviceRebootResponse>(
				`/portal/accounts/${accountId}/rebootDevice`,
				{
					method: 'POST',
					body: {
						accountId,
						...payload
					},
					...options
				}
			)
		}
	}
}
